import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, Key, Mail } from 'lucide-react';
import { useActivation } from '@/hooks/useActivation';
import { cn } from '@/utils/functions/cn';

const activationSchema = z.object({
  activationCode: z.string()
    .min(8, 'Código deve ter pelo menos 8 caracteres')
    .max(50, 'Código muito longo')
    .regex(/^[A-Z0-9-]+$/, 'Código deve conter apenas letras maiúsculas, números e hífens'),
  email: z.string()
    .email('Email inválido')
    .min(1, 'Email é obrigatório'),
  confirmEmail: z.string()
    .email('Email inválido')
    .min(1, 'Confirmação de email é obrigatória'),
}).refine((data) => data.email === data.confirmEmail, {
  message: "Emails não coincidem",
  path: ["confirmEmail"],
});

type ActivationFormData = z.infer<typeof activationSchema>;

export const ActivationForm = () => {
  const { activate, isActivating, activationData, activationError } = useActivation();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch
  } = useForm<ActivationFormData>({
    resolver: zodResolver(activationSchema),
    mode: 'onChange'
  });

  const onSubmit = async (data: ActivationFormData) => {
    try {
      await activate({
        activationCode: data.activationCode.toUpperCase(),
        email: data.email,
        deviceId: navigator.userAgent // Simple device identification
      });
      setShowSuccess(true);
      reset();
    } catch (error) {
      console.error('Activation error:', error);
    }
  };

  if (showSuccess && activationData?.success) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-600 dark:text-green-400">
            Ativação Concluída!
          </CardTitle>
          <CardDescription>
            Sua conta foi ativada com sucesso
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {activationData.data && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plano:</span>
                <span className="font-medium">{activationData.data.plan}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ativado em:</span>
                <span className="font-medium">
                  {new Date(activationData.data.activatedAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {activationData.data.expiresAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expira em:</span>
                  <span className="font-medium">
                    {new Date(activationData.data.expiresAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          )}
          <Button 
            onClick={() => setShowSuccess(false)} 
            className="w-full"
          >
            Ativar Outra Conta
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Key className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-2xl font-bold">Ativar Conta</CardTitle>
        <CardDescription>
          Digite seu código de ativação para começar a usar o MoovaTV
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Activation Code */}
          <div className="space-y-2">
            <Label htmlFor="activationCode">Código de Ativação</Label>
            <Input
              id="activationCode"
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className={cn(
                "font-mono text-center tracking-wider uppercase",
                errors.activationCode && "border-red-500"
              )}
              {...register('activationCode')}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
                register('activationCode').onChange(e);
              }}
            />
            {errors.activationCode && (
              <p className="text-sm text-red-500">{errors.activationCode.message}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                className={cn(
                  "pl-10",
                  errors.email && "border-red-500"
                )}
                {...register('email')}
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          {/* Confirm Email */}
          <div className="space-y-2">
            <Label htmlFor="confirmEmail">Confirmar Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmEmail"
                type="email"
                placeholder="confirme seu email"
                className={cn(
                  "pl-10",
                  errors.confirmEmail && "border-red-500"
                )}
                {...register('confirmEmail')}
              />
            </div>
            {errors.confirmEmail && (
              <p className="text-sm text-red-500">{errors.confirmEmail.message}</p>
            )}
          </div>

          {/* Error Alert */}
          {activationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {activationError.message || 'Erro ao ativar conta. Tente novamente.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            disabled={!isValid || isActivating}
          >
            {isActivating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Ativando...
              </>
            ) : (
              'Ativar Conta'
            )}
          </Button>
        </form>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>Não tem um código de ativação?</p>
          <Button variant="link" className="p-0 h-auto font-normal">
            Entre em contato conosco
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
